/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo } from 'react';
import Media from 'react-media';

import { FormattedMessage, FormattedNumber } from 'js/lib/coursera.react-intl';

import { Grid, Typography2, breakpoints, typography2 } from '@coursera/cds-core';
import type { GridProps } from '@coursera/cds-core';

import LazyImgix from 'bundles/page/components/shared/LazyImgix';
import useUserAgent from 'bundles/page/hooks/useUserAgent';

import _t from 'i18n!nls/xdp';

interface Props {
  imageSrc?: string | null;
  mobileImageSrc?: string | null;
  borderColor?: string | null;
  jobTitle?: string | null;
  medianSalary?: number | null;
  numJobsAvailable?: number | null;
  careerGrowthInPercentage?: number | null;
  currencyCode?: string | null;
  countryName?: string | null;
}

const styles = {
  desktopContainer: css`
    height: 100%;
    position: relative;
  `,
  mobileContainer: (borderColor?: string | null) => css`
    position: relative;
    border: 2px solid ${borderColor ?? 'var(--cds-color-neutral-stroke-primary-weak)'};
    border-radius: calc(var(--cds-border-radius-200) + var(--cds-border-radius-50));
    display: flex;
    justify-content: space-between;
  `,
  mobileImageBorderRadius: css`
    border-radius: 0 calc(var(--cds-border-radius-200) + var(--cds-border-radius-50))
      calc(var(--cds-border-radius-200) + var(--cds-border-radius-50)) 0;
  `,
  desktopDatabox: (borderColor?: string | null) => css`
    position: absolute;
    bottom: 0;
    right: var(--cds-spacing-600);
    width: auto;
    border: 2px solid ${borderColor ?? 'var(--cds-color-neutral-stroke-primary-weak)'};
    border-radius: var(--cds-border-radius-100);
    margin-bottom: var(--cds-spacing-400);
    padding: var(--cds-spacing-300);
    background-color: var(--cds-color-neutral-background-primary);

    & > :not(:last-child) {
      padding-right: var(--cds-spacing-200);
    }

    & > :not(:first-child) {
      padding-left: var(--cds-spacing-200);
      border-left: 1px solid var(--cds-color-grey-50);
    }
  `,
  mobileDataBox: css`
    padding: var(--cds-spacing-200);
    z-index: 1;
    display: flex;
    gap: var(--cds-spacing-100);
    flex-direction: column;
    justify-content: center;
  `,
  inlineInfo: css`
    & > span {
      display: flex;
      align-items: center;
      gap: var(--cds-spacing-100);
    }
  `,
  imageWrapper: css`
    div {
      height: 100%;
      width: auto;
      padding-bottom: unset;
      max-width: none;
    }

    img {
      max-width: none !important;
      max-height: none !important;
      height: 100%;
      width: auto;
      float: right;
    }
  `,
  dataTitle: css`
    ${breakpoints.down('sm')} {
      ${typography2.titleMedium}
    }
  `,
};

const PathwaysDataBox = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      imageSrc,
      mobileImageSrc,
      borderColor,
      jobTitle,
      medianSalary,
      numJobsAvailable,
      currencyCode,
      countryName,
      careerGrowthInPercentage,
    },
    ref
  ) => {
    const userAgent = useUserAgent();

    const dataRenderers = useMemo(() => {
      type DataBoxRendererConfig = {
        isInline?: boolean;
        isMobile?: boolean;
      };

      const value = [];
      if (medianSalary !== null && medianSalary !== undefined && jobTitle && currencyCode) {
        value.push(({ isInline, isMobile }: DataBoxRendererConfig) => (
          <Typography2 component="span" variant="bodySecondary" color="supportText" css={isInline && styles.inlineInfo}>
            <FormattedMessage
              message={
                isMobile
                  ? _t('{salary} median entry-level salary¹')
                  : _t('{salary} median entry-level salary for {jobTitle}¹')
              }
              salary={
                <Typography2
                  component="div"
                  variant="titleSmall"
                  display={isInline ? 'inline' : 'initial'}
                  css={styles.dataTitle}
                >
                  <FormattedNumber
                    value={medianSalary}
                    // eslint-disable-next-line react/style-prop-object
                    style="currency"
                    currency={currencyCode}
                    maximumFractionDigits={0}
                    minimumFractionDigits={0}
                  />
                </Typography2>
              }
              jobTitle={jobTitle}
            />
          </Typography2>
        ));
      }

      if (numJobsAvailable !== null && numJobsAvailable !== undefined && jobTitle && countryName) {
        value.push(({ isInline, isMobile }: DataBoxRendererConfig) => (
          <Typography2 component="span" variant="bodySecondary" color="supportText" css={isInline && styles.inlineInfo}>
            <FormattedMessage
              message={
                isMobile
                  ? _t('{numberOfJobs} job openings in {countryName}¹')
                  : _t('{numberOfJobs} job openings for {jobTitle}  in {countryName}¹')
              }
              countryName={countryName}
              numberOfJobs={
                <Typography2
                  component="div"
                  variant="titleSmall"
                  display={isInline ? 'inline' : 'initial'}
                  css={styles.dataTitle}
                >
                  <FormattedNumber value={numJobsAvailable} />
                </Typography2>
              }
              jobTitle={jobTitle}
            />
          </Typography2>
        ));
      }

      if (careerGrowthInPercentage && currencyCode?.toUpperCase() === 'USD') {
        value.push(({ isInline }: DataBoxRendererConfig) => (
          <Typography2 component="span" variant="bodySecondary" color="supportText" css={isInline && styles.inlineInfo}>
            <FormattedMessage
              message={_t('{careerGrowthInPercentage} of certificate grads report career improvement²')}
              careerGrowthInPercentage={
                <Typography2
                  component="div"
                  variant="titleSmall"
                  display={isInline ? 'inline' : 'initial'}
                  css={styles.dataTitle}
                >
                  {/* eslint-disable-next-line react/style-prop-object */}
                  <FormattedNumber value={careerGrowthInPercentage / 100} style="percent" />
                </Typography2>
              }
            />
          </Typography2>
        ));
      }

      return value;
    }, [jobTitle, medianSalary, numJobsAvailable, careerGrowthInPercentage, currencyCode, countryName]);

    const isInlineInfo = dataRenderers.length <= 1;
    const numberOfColumns = dataRenderers.length > 0 ? ((12 / dataRenderers.length) as GridProps['xs']) : 12;

    const renderMobileDataBox = () => {
      if (!dataRenderers.length) return null;
      return (
        <div css={styles.mobileContainer(borderColor)} ref={ref}>
          <div css={styles.mobileDataBox}>
            {dataRenderers.map((renderer, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <React.Fragment key={index}>{renderer({ isMobile: true })}</React.Fragment>
            ))}
          </div>
          {mobileImageSrc && (
            <div css={styles.imageWrapper}>
              <LazyImgix
                src={mobileImageSrc}
                alt=""
                maxHeight={259}
                maxWidth={164}
                imgParams={{ crop: 'right', fit: 'fill' }}
                css={styles.mobileImageBorderRadius}
              />
            </div>
          )}
        </div>
      );
    };

    const renderDesktopDataBox = () => {
      return (
        <div css={styles.desktopContainer}>
          {imageSrc && (
            <div css={styles.imageWrapper}>
              <LazyImgix src={imageSrc} alt="" maxHeight={438} maxWidth={690} imgParams={{ fit: 'fill' }} />
            </div>
          )}
          {dataRenderers.length > 0 && (
            <Grid container css={styles.desktopDatabox(borderColor)} ref={ref}>
              {dataRenderers.map((renderer, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Grid item xs={numberOfColumns} key={index}>
                  {renderer({ isInline: isInlineInfo })}
                </Grid>
              ))}
            </Grid>
          )}
        </div>
      );
    };

    return (
      <Media query={{ maxWidth: breakpoints.values.md - 1 }} defaultMatches={userAgent?.isMobileBrowser}>
        {(matches) => (
          <React.Fragment>
            {matches && renderMobileDataBox()}
            {!matches && renderDesktopDataBox()}
          </React.Fragment>
        )}
      </Media>
    );
  }
);

export default PathwaysDataBox;
